import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { animated, useSpring } from 'react-spring';
import {
  Row,
  Col,
  Input,
  notification,
  Progress,
} from 'antd';
import {
  FolderAddOutlined,
  FileAddOutlined,
  DeleteOutlined,
  SwapOutlined,
  CopyOutlined,
  LeftOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { PropTypes } from 'prop-types';

import FileDeleteModal from './FileDeleteModal';
import FileAddButton from './FileAddButton';

import NuxPopover from '../nux/NuxPopover';
import {
  FILES_PAGE_NUX_STEP_1,
  FILES_PAGE_NUX_STEP_2,
  FILES_PAGE_NUX_STEP_3,
  FILES_PAGE_NUX_STEP_1_SEARCH_TEXT,
  FILES_PAGE_NUX_STEP_1_ADD_TEXT,
  FILES_PAGE_NUX_STEP_3_MOVE_TEXT,
} from '../nux/nux.constants';

import BorderlessButton from '../common/buttons/BorderlessButton';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import HoverHelp from '../common/HoverHelp';

import {
  downloadMultipleFiles,
} from './fileHelpers';

const ROW_HEIGHT = 32;
const MARGIN = 8;
const FULL_ROW_HEIGHT = ROW_HEIGHT + 2 * MARGIN;
const MAX_SELECTED = 10;
const NOTIFICATION_KEY = 'fileUploadNotification';

const updateNotification = ({
  notificationMsg,
  percent,
}) => (
  notification.open({
    key: NOTIFICATION_KEY,
    placement: 'bottomRight',
    message: notificationMsg,
    description: (
      <Progress
        type="circle"
        percent={percent}
      />
    ),
  })
);

export default function FileListHeader({
  onSearch,
  searchVal,
  onAddFolder,
  onAddFile,
  onDelete,
  onMove,
  onCopy,
  selected = [],
  onBack,
  hasWritePerms,
  promptForPasswordOnDelete = true,
  showAddFileButton = true,
}) {
  const activeNuxAction = useSelector((state) => state.nux.activeNuxAction);
  const company = useSelector((state) => state.settings.company);
  const hasRedesign = company?.settings?.uxRedesignPt1;

  const showNuxStep1 = activeNuxAction === FILES_PAGE_NUX_STEP_1;
  const showNuxStep3 = activeNuxAction === FILES_PAGE_NUX_STEP_3;
  const [stateShowNuxStep3, setShowNuxStep3] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [addStyle, setAddStyle] = useSpring(() => ({ }));
  const [selectedStyle, setSelectedStyle] = useSpring(() => ({ }));
  const [downloadingFiles, setDownloadingFiles] = useState(false);

  const onDownload = useCallback(async () => {
    const filesToDownload = selected
      .filter((s) => s.type !== 'folder')
      .map((s) => ({
        fullPath: s.fullPath,
        name: s.name,
      }));

    await downloadMultipleFiles({
      filesToDownload,
      setDownloadingFiles,
      updateNotification,
      notificationKey: NOTIFICATION_KEY,
    });
  }, [selected]);

  const showDelete = useCallback(() => setShowDeleteModal(true), []);
  const hideDelete = useCallback(() => setShowDeleteModal(false), []);
  const onChange = useCallback((e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    onSearch(value);
  }, [onSearch]);

  useEffect(() => {
    const hasSelected = selected.length || showNuxStep3;
    const transform = `translateY(${hasSelected ? -ROW_HEIGHT : 0}px)`;
    setAddStyle({
      opacity: hasSelected ? 0 : 1,
      transform,
    });
    setSelectedStyle({
      opacity: hasSelected ? 1 : 0,
      transform,
    });
  }, [selected, setAddStyle, setSelectedStyle, showNuxStep3]);

  useEffect(() => {
    if (showNuxStep3) {
      setTimeout(() => setShowNuxStep3(true), 500);
    } else {
      setShowNuxStep3(false);
    }
  }, [showNuxStep3]);

  const isCopyMoveDisabled = useMemo(() => {
    const isMaxSelected = selected?.length > MAX_SELECTED;
    const hasFolder = selected?.some((file) => file.type === 'folder');
    return isMaxSelected || hasFolder;
  }, [selected]);

  const selectedModifier = showNuxStep3 || selected.length === 1 ? '' : 's';
  return (
    <div style={{
      height:
      ROW_HEIGHT,
      overflow: 'hidden',
      marginBottom: 15,
      zIndex: showNuxStep1 || showNuxStep3 ? 100 : 1,
      position: 'relative',
      marginTop: 15,
    }}
    >
      <Row align="middle" gutter={10} justify="space-between" style={{ padding: '0px 0xp' }}>
        <animated.div style={{
          flex: 1,
          top: 0,
          padding: '0px 10px',
          overflow: 'hidden',
          ...addStyle,
        }}
        >
          <Row gutter={10} justify="space-between">
            <Col style={{ flexGrow: 1, maxWidth: 550, paddingLeft: hasRedesign ? 0 : 22 }}>
              <Row justify="start" gutter={20}>
                {onBack && (
                  <Col flex="35px">
                    <BorderlessButton
                      style={{ backgroundColor: 'transparent', padding: 0 }}
                      iconNode={<LeftOutlined style={{ padding: 0, margin: 0 }} />}
                      onClick={onBack}
                    />
                  </Col>
                )}
                <Col flex="auto">
                  <NuxPopover
                    placement="bottom"
                    text={FILES_PAGE_NUX_STEP_1_SEARCH_TEXT}
                    visible={showNuxStep1}
                    nextAction={FILES_PAGE_NUX_STEP_2}
                  >
                    <Input.Search
                      type="search"
                      value={searchVal}
                      disabled={selected.length}
                      style={{ width: '100%', pointerEvents: showNuxStep1 ? 'none' : 'auto' }}
                      className="searchbar"
                      placeholder="Search"
                      allowClear
                      onChange={onChange}
                    />
                  </NuxPopover>
                </Col>
              </Row>
            </Col>
            <Col>
              {hasWritePerms
                && (
                <NuxPopover
                  text={FILES_PAGE_NUX_STEP_1_ADD_TEXT}
                  visible={showNuxStep1}
                  nextAction={FILES_PAGE_NUX_STEP_2}
                >
                  <Row justify="end" gutter={12}>
                    <Col>
                      <FileAddButton
                        title="Add Folder"
                        icon={<FolderAddOutlined />}
                        onClick={onAddFolder}
                        disabled={selected.length}
                        style={{ pointerEvents: showNuxStep1 ? 'none' : 'auto' }}
                      />
                    </Col>
                    { showAddFileButton && (
                    <Col>
                      <FileAddButton
                        title="Add Files"
                        icon={<FileAddOutlined />}
                        onClick={onAddFile}
                        disabled={selected.length}
                        style={{ pointerEvents: showNuxStep1 ? 'none' : 'auto' }}
                      />
                    </Col>
                    )}
                  </Row>
                </NuxPopover>
                )}
            </Col>
          </Row>
        </animated.div>
      </Row>
      <animated.div style={{
        backgroundColor: 'transparent',
        width: '100%',
        height: FULL_ROW_HEIGHT,
        ...selectedStyle,
      }}
      >

        <Row justify="end" align="middle" gutter={18}>
          <Col>
            Selected
            {' '}
            <b>{showNuxStep3 ? 1 : selected.length}</b>
            {' '}
            item
            {selectedModifier}
          </Col>
          {isCopyMoveDisabled && (
            <Col>
              <HoverHelp
                content={(
                  <div style={{ width: 300 }}>
                    Can only copy/move up to {MAX_SELECTED} files at a time
                  </div>
                )}
                Icon={ExclamationCircleOutlined}
              />
            </Col>
          )}
          <Col>
            <OnTraccrButton
              type="cancel"
              title="Download"
              icon={<DownloadOutlined />}
              onClick={onDownload}
              disabled={!selected.length || selected.length > 100}
              style={{ pointerEvents: showNuxStep3 ? 'none' : 'auto' }}
              loading={downloadingFiles}
            />
          </Col>
          <Col>
            <OnTraccrButton
              type="cancel"
              title="Copy"
              icon={<CopyOutlined />}
              onClick={onCopy}
              disabled={!selected.length || isCopyMoveDisabled}
              style={{ pointerEvents: showNuxStep3 ? 'none' : 'auto' }}
            />
          </Col>
          <Col>
            <NuxPopover
              visible={stateShowNuxStep3}
              text={FILES_PAGE_NUX_STEP_3_MOVE_TEXT}
              placement="bottom"
            >
              <OnTraccrButton
                type="cancel"
                title="Move"
                icon={<SwapOutlined />}
                onClick={onMove}
                disabled={!selected.length || isCopyMoveDisabled}
                style={{ pointerEvents: showNuxStep3 ? 'none' : 'auto' }}
              />
            </NuxPopover>
          </Col>
          <Col>
            <OnTraccrButton
              title="Delete"
              type="back"
              icon={<DeleteOutlined />}
              onClick={showDelete}
              disabled={!selected.length}
              style={{ pointerEvents: showNuxStep3 ? 'none' : 'auto' }}
            />
          </Col>
        </Row>

      </animated.div>
      <FileDeleteModal
        title={`Delete ${selected.length} item${selectedModifier}`}
        onClose={hideDelete}
        visible={showDeleteModal}
        onDelete={onDelete}
        promptForPasswordOnDelete={promptForPasswordOnDelete}
      />
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
FileListHeader.propTypes = {
  onSearch: PropTypes.func.isRequired,
  searchVal: PropTypes.string,
  onAddFolder: PropTypes.func.isRequired,
  onAddFile: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  selected: PropTypes.array,
  onBack: PropTypes.func,
  hasWritePerms: PropTypes.bool.isRequired,
  promptForPasswordOnDelete: PropTypes.bool,
  showAddFileButton: PropTypes.bool,
};

FileListHeader.defaultProps = {
  searchVal: '',
  selected: [],
  onBack: null,
  promptForPasswordOnDelete: true,
  showAddFileButton: true,
};
